// src/runtime/KnowledgeEvolutionRuntime/service.ts
// ============================================
// KnowledgeEvolutionService - 知识演化编排逻辑
//
// 依据白皮书第十五章《Knowledge Evolution Protocol》：
//   Hermes 对话 / Runtime 日志 / 审核反馈 / Git Commit / 客户跟进记录
//     ↓ Knowledge Extractor（提取）
//     ↓ Knowledge Inbox（30_Inbox/，全自动写入）
//     ↓ Knowledge Evolver（夜间整理：去重/聚类/合并/摘要）
//     ↓ Knowledge Validator（风险分级：低/中/高）
//     ↓ Knowledge Writer（低风险自动入库 / 中高风险等待 CEO 确认）
//     ↓ "今天公司学到了什么"日报（每天 23:00 自动生成）
//
// 设计原则：
//   1. 全部候选知识写入 Vault（30_Inbox/），不在内存/日志中丢失（Rule 05）
//   2. 不生成假数据（Rule 02）：无来源数据时返回空，而非 mock
//   3. 低风险自动通过，中高风险必须有 CEO 人工确认（Rule 03 / 权限矩阵）
// ============================================

import { RuntimeName } from '../../types/runtime';
import type { RuntimeManager } from '../RuntimeManager';
import { vaultConfig } from '../shared/vaultConfig';
import config from './config';
import {
  KnowledgeSourceType,
  KnowledgeEvolutionQuery,
  KnowledgeEvolutionCommand,
  type CandidateKnowledge,
  type CandidateStatus,
  type CandidateRiskLevel,
  type DailyKnowledgeReport,
  type KnowledgeEvolutionStats,
  type KnowledgeEvolutionResult,
} from './types';
import { KnowledgeExtractor, type RawKnowledgeSource } from './extractor';
import { KnowledgeValidator } from './validator';
import { KnowledgeEvolver } from './evolver';
import { KnowledgeWriter } from './writer';

// ============================================
// KnowledgeEvolutionService
// ============================================

export class KnowledgeEvolutionService {
  private logger: Console;
  private runtimeManager: RuntimeManager | null;

  private extractor = new KnowledgeExtractor();
  private validator = new KnowledgeValidator();
  private evolver = new KnowledgeEvolver();
  private writer = new KnowledgeWriter();

  /** 候选知识内存态（Vault 30_Inbox/ 是持久真相源） */
  private candidates: CandidateKnowledge[] = [];
  /** 日报缓存 */
  private reports: DailyKnowledgeReport[] = [];
  /** 上次运行时间 */
  private lastRunAt?: string;
  /** 初始化标记 */
  private initialized = false;

  constructor(runtimeManager: RuntimeManager | null = null, logger: Console = console) {
    this.runtimeManager = runtimeManager;
    this.logger = logger;
  }

  // ============================================
  // 初始化
  // ============================================

  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
    this.logger.info('[KnowledgeEvolutionService] ✅ 初始化完成（后台运行，不占用 Workspace）');
  }

  // ============================================
  // 查询接口
  // ============================================

  async query<T>(
    type: KnowledgeEvolutionQuery,
    params: Record<string, any> = {}
  ): Promise<KnowledgeEvolutionResult<T>> {
    if (!this.initialized) await this.initialize();
    try {
      switch (type) {
        case KnowledgeEvolutionQuery.ListCandidates: {
          const { status, risk, source } = params;
          let list = this.candidates;
          if (status) list = list.filter((c) => c.status === status);
          if (risk) list = list.filter((c) => c.riskLevel === risk);
          if (source) list = list.filter((c) => c.source === source);
          const inboxFiles = await this.writer.listInboxCandidates();
          return this.ok({
            candidates: list,
            inboxFiles,
            total: list.length,
          } as T);
        }
        case KnowledgeEvolutionQuery.GetCandidate: {
          const found = this.candidates.find((c) => c.id === params.id);
          return found
            ? this.ok(found as T)
            : this.fail(`候选知识不存在: ${params.id}`);
        }
        case KnowledgeEvolutionQuery.GetStats:
          return this.ok(this.getStats() as T);
        case KnowledgeEvolutionQuery.GetDailyReport: {
          const date = params.date || this.today();
          const report = this.reports.find((r) => r.date === date);
          return report ? this.ok(report as T) : this.ok(null as unknown as T);
        }
        case KnowledgeEvolutionQuery.ListReports:
          return this.ok(this.reports as T);
        default:
          return this.fail(`未知的查询类型: ${type}`);
      }
    } catch (err: any) {
      return this.fail(err?.message || String(err));
    }
  }

  // ============================================
  // 命令接口
  // ============================================

  async execute<T>(
    command: KnowledgeEvolutionCommand,
    params: Record<string, any> = {}
  ): Promise<KnowledgeEvolutionResult<T>> {
    if (!this.initialized) await this.initialize();
    try {
      switch (command) {
        case KnowledgeEvolutionCommand.RunDailyEvolution: {
          const result = await this.runDailyEvolution();
          return this.ok(result as unknown as T);
        }
        case KnowledgeEvolutionCommand.ExtractNow: {
          const extracted = await this.extractFromSources();
          return this.ok({ extracted: extracted.length } as unknown as T);
        }
        case KnowledgeEvolutionCommand.ConfirmCandidate: {
          const id = params.id;
          const candidate = this.candidates.find((c) => c.id === id);
          if (!candidate) return this.fail(`候选知识不存在: ${id}`);
          return this.ok((await this.confirmCandidate(candidate)) as unknown as T);
        }
        case KnowledgeEvolutionCommand.RejectCandidate: {
          const id = params.id;
          const candidate = this.candidates.find((c) => c.id === id);
          if (!candidate) return this.fail(`候选知识不存在: ${id}`);
          return this.ok(
            (await this.rejectCandidate(candidate, params.feedback)) as unknown as T
          );
        }
        case KnowledgeEvolutionCommand.Reset:
          this.candidates = [];
          this.reports = [];
          this.lastRunAt = undefined;
          return this.ok({ reset: true } as unknown as T);
        default:
          return this.fail(`未知的命令: ${command}`);
      }
    } catch (err: any) {
      return this.fail(err?.message || String(err));
    }
  }

  // ============================================
  // 核心流程：一次完整的知识演化（每天 23:00 调度）
  // ============================================

  /**
   * 运行完整知识演化流程：
   *   1. 收集来源（Hermes 对话 / Runtime 日志 / 审核反馈 / Git Commit / 客户跟进）
   *   2. Knowledge Extractor 提取候选知识
   *   3. Knowledge Validator 风险分级
   *   4. Knowledge Evolver 夜间整理（去重/聚类/合并）
   *   5. Knowledge Writer 写入 30_Inbox/（低风险同时自动入库正式知识库）
   *   6. 生成"今天公司学到了什么"日报
   */
  private async runDailyEvolution() {
    const date = this.today();
    this.lastRunAt = new Date().toISOString();

    // 1-2. 提取
    const extracted = await this.extractFromSources();
    if (extracted.length === 0) {
      // 没有候选知识也生成日报（空报告，符合 Rule 02 不造假数据）
      const report: DailyKnowledgeReport = {
        date,
        generatedAt: new Date().toISOString(),
        newCandidates: 0,
        autoApproved: 0,
        needsReview: 0,
        needsCeo: 0,
        insights: [],
        vaultPath: `${config.reportDir}/今日所学-${date}.md`,
      };
      const writeResult = await this.writer.writeDailyReport(report);
      this.reports.unshift(report);
      return {
        date,
        extracted: 0,
        report,
        reportWrite: writeResult,
      };
    }

    // 3. 风险分级
    const validated = this.validator.validate(extracted);
    // 4. 夜间整理
    const evolved = this.evolver.evolve(validated);
    this.candidates = [...this.candidates, ...evolved];

    // 5. 写入 Inbox（全部），低风险自动入库正式知识库
    const inboxResult = await this.writer.writeCandidatesToInbox(evolved, date);
    let autoApproved = 0;
    let needsReview = 0;
    let needsCeo = 0;
    for (const c of evolved) {
      if (this.validator.canAutoApprove(c)) {
        const r = await this.writer.writeToFormalKnowledgeBase(c, date);
        c.status = 'auto_approved';
        c.processedAt = new Date().toISOString();
        if (r.success) autoApproved++;
      } else if (c.riskLevel === 'medium') {
        c.status = 'needs_review';
        needsReview++;
      } else {
        c.status = 'needs_ceo';
        needsCeo++;
      }
    }

    // 6. 日报
    const report: DailyKnowledgeReport = {
      date,
      generatedAt: new Date().toISOString(),
      newCandidates: evolved.length,
      autoApproved,
      needsReview,
      needsCeo,
      insights: evolved.slice(0, 10).map((c) => ({
        title: c.title,
        summary: c.summary,
        source: c.source,
      })),
      vaultPath: `${config.reportDir}/今日所学-${date}.md`,
    };
    const reportWrite = await this.writer.writeDailyReport(report);
    this.reports.unshift(report);

    this.logger.info(
      `[KnowledgeEvolutionService] 📊 知识演化完成：新增 ${evolved.length} 条 / 自动入库 ${autoApproved} / 待确认 ${needsReview} / 必须确认 ${needsCeo}`
    );
    return { date, extracted: evolved.length, report, reportWrite, inboxResult };
  }

  /** 收集来源并提取候选知识 */
  private async extractFromSources(): Promise<CandidateKnowledge[]> {
    const rawSources = await this.collectRawSources();
    if (rawSources.length === 0) {
      this.logger.info('[KnowledgeEvolutionService] 未收集到知识来源，跳过提取（不造假数据）');
      return [];
    }
    const extracted = this.extractor.extractAll(rawSources, this.today());
    return extracted;
  }

  // ============================================
  // 来源收集（真实调用，无数据返回空）
  // ============================================

  /** 从各 Runtime 收集真实来源数据（每个来源独立 try/catch，单个失败不阻塞） */
  private async collectRawSources(): Promise<RawKnowledgeSource[]> {
    const sources: RawKnowledgeSource[] = [];
    const rm = this.runtimeManager;

    // 1. Hermes 对话 → HermesRuntime（对话留痕数据）
    if (rm) {
      try {
        const resp = await rm.query(RuntimeName.Hermes, {
          type: 'list_conversations',
          limit: 5,
        } as any);
        const data = this.unwrapData(resp);
        const convs = Array.isArray(data) ? data : data?.conversations ?? [];
        for (const conv of convs.slice(0, 5)) {
          const text = String(conv?.content || conv?.message || '');
          if (text.trim().length >= config.minSentenceLength) {
            sources.push({
              source: KnowledgeSourceType.HermesDialogue,
              ref: String(conv?.id || conv?.conversationId || 'hermes'),
              text,
            });
          }
        }
      } catch (err) {
        this.logger.debug('[KnowledgeEvolution] Hermes 对话源不可用: ' + String(err));
      }

      // 2. 客户跟进记录 → CRMRuntime
      try {
        const resp = await rm.query(RuntimeName.CRM, { type: 'list', limit: 10 } as any);
        const data = this.unwrapData(resp);
        const customers = Array.isArray(data) ? data : data?.customers ?? data?.data ?? [];
        for (const cust of customers.slice(0, 10)) {
          const notes = String(
            cust?.notes || cust?.followUpNotes || cust?.lastFollowUp || ''
          );
          if (notes.trim().length >= config.minSentenceLength) {
            sources.push({
              source: KnowledgeSourceType.CustomerFollowUp,
              ref: String(cust?.id || cust?.customerId || cust?.name || 'crm'),
              text: notes,
            });
          }
        }
      } catch (err) {
        this.logger.debug('[KnowledgeEvolution] CRM 跟进源不可用: ' + String(err));
      }

      // 3. 审核反馈 / 复盘 → KnowledgeRuntime（Reviews 目录）
      try {
        const resp = await rm.query(RuntimeName.Knowledge, {
          type: 'list_all_notes',
          category: 'Reviews',
        } as any);
        const notes = this.unwrapData(resp) || [];
        const arr = Array.isArray(notes) ? notes : [];
        for (const n of arr.slice(0, 5)) {
          const content = String(n?.content || n?.summary || '');
          if (content.trim().length >= config.minSentenceLength) {
            sources.push({
              source: KnowledgeSourceType.ReviewFeedback,
              ref: String(n?.filePath || n?.id || 'review'),
              text: content,
            });
          }
        }
      } catch (err) {
        this.logger.debug('[KnowledgeEvolution] 审核反馈源不可用: ' + String(err));
      }
    }

    // 4. Runtime 日志：扫描 10_Operations（运营层）/Runtime 目录（浏览器通过 BrowserVaultReader）
    try {
      const reader = (await import('../shared/BrowserVaultReader')).BrowserVaultReader.getInstance();
      const files = await reader.listFiles('10_Operations（运营层）/Runtime');
      for (const f of files.slice(0, 5)) {
        const content = await reader.readFile(f.path);
        if (content && content.trim().length >= config.minSentenceLength) {
          sources.push({
            source: KnowledgeSourceType.RuntimeLog,
            ref: f.path,
            text: content.slice(0, 2000),
          });
        }
      }
    } catch (err) {
      this.logger.debug('[KnowledgeEvolution] Runtime 日志源不可用: ' + String(err));
    }
    // 5. Git Commit：实际扫描 Vault 的 git 提交记录（白皮书第十五章知识来源之一）
    //    - Node 环境：直接执行 git log 扫描本地 Vault 仓库
    //    - 浏览器环境：通过 /vault-api?action=git-log 读取服务器 Vault 仓库的最近提交
    try {
      if (!vaultConfig.isBrowser && typeof process !== 'undefined') {
        const cp: any = await import('child_process');
        const gitLog = cp.execSync(
          'git log -15 --date=short --pretty=format:%ad|%s',
          { cwd: vaultConfig.vaultRoot, encoding: 'utf-8', timeout: 8000 }
        );
        if (gitLog && String(gitLog).trim().length >= config.minSentenceLength) {
          sources.push({
            source: KnowledgeSourceType.GitCommit,
            ref: 'local-vault-git',
            text: String(gitLog).slice(0, 2000),
          });
        }
      } else {
        const resp = await fetch('/vault-api?action=git-log&limit=15');
        if (resp.ok) {
          const json = await resp.json();
          if (json?.success && Array.isArray(json.commits)) {
            const text = json.commits
              .map((c: any) => `${c.date || ''} ${c.message || ''}`)
              .filter(Boolean)
              .join('\n');
            if (text.trim().length >= config.minSentenceLength) {
              sources.push({
                source: KnowledgeSourceType.GitCommit,
                ref: 'server-vault-git',
                text: text.slice(0, 2000),
              });
            }
          }
        }
      }
    } catch (err) {
      this.logger.debug('[KnowledgeEvolution] Git Commit 源不可用: ' + String(err));
    }


    return sources;
  }

  // ============================================
  // CEO 处理：确认入库 / 拒绝归档
  // ============================================

  /** CEO 确认候选知识入库（中风险/高风险处理后写入正式知识库） */
  private async confirmCandidate(candidate: CandidateKnowledge) {
    const date = this.today();
    const writeResult = await this.writer.writeToFormalKnowledgeBase(candidate, date);
    if (writeResult.success) {
      candidate.status = 'approved';
      candidate.processedAt = new Date().toISOString();
    }
    return { id: candidate.id, status: candidate.status, writeResult };
  }

  /** CEO 拒绝候选知识归档 */
  private async rejectCandidate(candidate: CandidateKnowledge, feedback?: string) {
    candidate.status = 'rejected';
    candidate.processedAt = new Date().toISOString();
    candidate.feedback = feedback || 'CEO 拒绝归档';
    return { id: candidate.id, status: candidate.status, feedback: candidate.feedback };
  }

  // ============================================
  // 统计与工具
  // ============================================

  private getStats(): KnowledgeEvolutionStats {
    const byStatus: Partial<Record<CandidateStatus, number>> = {};
    const byRisk: Partial<Record<CandidateRiskLevel, number>> = {};
    const bySource: Partial<Record<KnowledgeSourceType, number>> = {};
    for (const c of this.candidates) {
      byStatus[c.status] = (byStatus[c.status] || 0) + 1;
      byRisk[c.riskLevel] = (byRisk[c.riskLevel] || 0) + 1;
      bySource[c.source] = (bySource[c.source] || 0) + 1;
    }
    return {
      totalCandidates: this.candidates.length,
      byStatus,
      byRisk,
      bySource,
      lastRunAt: this.lastRunAt,
      lastReport: this.reports[0],
    };
  }

  /** 解包 RuntimeResponse（兼容双层结构） */
  private unwrapData(resp: any): any {
    if (!resp) return null;
    if (resp.success && resp.data !== undefined) return resp.data;
    if (resp.success) return resp.data ?? null;
    return resp.data ?? resp;
  }

  /** 当前日期 YYYY-MM-DD */
  private today(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
  }

  private ok<T>(data: T): KnowledgeEvolutionResult<T> {
    return { success: true, data, timestamp: new Date().toISOString() };
  }

  private fail<T>(error: string): KnowledgeEvolutionResult<T> {
    return { success: false, error, timestamp: new Date().toISOString() };
  }
}

export default KnowledgeEvolutionService;
